import { useCallback, useEffect, useState } from "react";

function getFullscreenElement(): Element | null {
  const doc = document as Document & { webkitFullscreenElement?: Element | null };
  return document.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

async function enterFullscreen(): Promise<void> {
  const el = document.documentElement;
  try {
    if (el.requestFullscreen) {
      await el.requestFullscreen();
      return;
    }
    const w = el as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> | void };
    if (w.webkitRequestFullscreen) {
      await Promise.resolve(w.webkitRequestFullscreen());
    }
  } catch {
    /* Browsers may reject without a user gesture or if unsupported. */
  }
}

async function leaveFullscreen(): Promise<void> {
  try {
    if (document.exitFullscreen) {
      await document.exitFullscreen();
      return;
    }
    const doc = document as Document & { webkitExitFullscreen?: () => Promise<void> | void };
    if (doc.webkitExitFullscreen) {
      await Promise.resolve(doc.webkitExitFullscreen());
    }
  } catch {
    /* ignore */
  }
}

/**
 * Requests fullscreen when the kiosk view mounts (works on some setups) and always
 * leaves fullscreen when unmounting. Exposes state and a manual retry for browsers
 * that require a tap.
 */
export function useVisitorManagementKioskFullscreen() {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const sync = useCallback(() => {
    setIsFullscreen(getFullscreenElement() !== null);
  }, []);

  useEffect(() => {
    sync();
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange" as keyof DocumentEventMap, sync as EventListener);
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange" as keyof DocumentEventMap, sync as EventListener);
    };
  }, [sync]);

  useEffect(() => {
    const id = window.setTimeout(() => {
      void enterFullscreen().then(sync);
    }, 0);
    return () => {
      window.clearTimeout(id);
      if (getFullscreenElement()) {
        void leaveFullscreen();
      }
    };
  }, [sync]);

  const requestFullscreen = useCallback(() => {
    void enterFullscreen().then(sync);
  }, [sync]);

  return { isFullscreen, requestFullscreen };
}
